import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { SAT_CONTEXTS, SAT_QUESTIONS, satContextEntry, pl } from "../../lib/partnerConstants";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Smile, Plus, Star, Trash2, Download, FileText, AlertTriangle, Award, TrendingUp, Send, Mail, Clock } from "lucide-react";

const emptySat = {
  partner_id: "", maison_id: "", context: "bilan_periodique", date: "", anonymous: false,
  q_speed: 0, q_welcome: 0, q_clarity: 0, q_ease_request: 0, q_followup: 0, q_availability: 0, q_general: 0, q_recommend: 0,
  comment: "", positives: "", improvements: "",
};

const alertStyle = {
  critical: { color: "#f87171", bg: "rgba(239,68,68,0.14)", icon: AlertTriangle },
  improve: { color: "#fbbf24", bg: "rgba(245,158,11,0.14)", icon: TrendingUp },
  ambassador: { color: "#34d399", bg: "rgba(16,185,129,0.14)", icon: Award },
};

function Stars({ value, onChange, testid }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" onClick={() => onChange(value === n ? 0 : n)} data-testid={`${testid}-${n}`}
          className="transition hover:scale-110">
          <Star className="h-5 w-5" style={{ fill: n <= value ? "#fbbf24" : "transparent", color: n <= value ? "#fbbf24" : "rgba(255,255,255,0.3)" }} />
        </button>
      ))}
    </div>
  );
}

export default function SatisfactionTab({ readOnly }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "fr";
  const [items, setItems] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [partners, setPartners] = useState([]);
  const [maisons, setMaisons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptySat);
  const [editId, setEditId] = useState(null);
  const [reqOpen, setReqOpen] = useState(false);
  const [req, setReq] = useState({ partner_id: "", maison_id: "", context: "bilan_periodique", email: "" });

  const load = useCallback(async () => {
    try {
      const [r, k] = await Promise.all([api.get("/crm/satisfaction"), api.get("/crm/satisfaction/kpis")]);
      setItems(r.data.items || []); setKpis(k.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const [pa, mm] = await Promise.all([api.get("/crm/partners"), api.get("/my/maisons")]);
        setPartners((pa.data.partners || pa.data || []).filter((x) => !x.archived));
        setMaisons(mm.data.maisons || []);
      } catch { /* non bloquant */ }
    })();
  }, []);

  const openNew = () => { setForm({ ...emptySat, date: new Date().toISOString().slice(0, 10) }); setEditId(null); setOpen(true); };
  const openEdit = (i) => { setForm({ ...emptySat, ...i }); setEditId(i.id); setOpen(true); };

  const save = async () => {
    if (!form.partner_id) return toast.error(t("crm.sat.partnerRequired"));
    try {
      if (editId) await api.put(`/crm/satisfaction/${editId}`, form);
      else await api.post("/crm/satisfaction", form);
      toast.success(t("crm.sat.saved")); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (i) => {
    if (!window.confirm(t("crm.sat.confirmDelete"))) return;
    try { await api.delete(`/crm/satisfaction/${i.id}`); toast.success(t("crm.sat.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a"); link.href = URL.createObjectURL(res.data); link.download = filename; link.click(); URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openReq = () => { setReq({ partner_id: "", maison_id: "", context: "bilan_periodique", email: "" }); setReqOpen(true); };
  const sendReq = async () => {
    if (!req.partner_id) return toast.error(t("crm.sat.partnerRequired"));
    if (!req.email.trim()) return toast.error(t("crm.sat.emailRequired"));
    try {
      const r = await api.post("/crm/satisfaction/request", { ...req, base_url: window.location.origin });
      toast.success(r.data.sent ? t("crm.sat.requestSent") : t("crm.sat.requestSaved"));
      setReqOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onReqPartner = (v) => setReq({ ...req, partner_id: v === "none" ? "" : v, email: partners.find((p) => p.id === v)?.email || req.email });

  const Kpi = ({ label, value, color }) => (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-bold" style={{ color }}>{value}</div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="crm-satisfaction-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Smile className="h-4 w-4 text-emerald-400" /> {t("crm.sat.title")}</h3>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => downloadFile("/crm/export/satisfaction.csv", "satisfaction.csv")} data-testid="crm-sat-export-csv"><Download className="h-4 w-4 mr-1" /> Excel/CSV</Button>
          <Button variant="outline" size="sm" onClick={() => downloadFile("/crm/export/satisfaction.pdf", "satisfaction.pdf")} data-testid="crm-sat-export-pdf"><FileText className="h-4 w-4 mr-1" /> PDF</Button>
          {!readOnly && <Button variant="outline" size="sm" onClick={openReq} data-testid="crm-sat-request"><Send className="h-4 w-4 mr-1" /> {t("crm.sat.requestBtn")}</Button>}
          {!readOnly && <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-500" size="sm" data-testid="crm-add-sat"><Plus className="h-4 w-4 mr-1" /> {t("crm.sat.addBtn")}</Button>}
        </div>
      </div>

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="crm-sat-kpis">
          <Kpi label={t("crm.sat.average")} value={`${kpis.average} / 5`} color="#34d399" />
          <Kpi label={t("crm.sat.count")} value={kpis.count} color="#60a5fa" />
          <Kpi label={t("crm.sat.unsatisfied")} value={kpis.unsatisfied} color="#f87171" />
          <Kpi label={t("crm.sat.ambassadors")} value={kpis.ambassadors} color="#a78bfa" />
          <Kpi label={t("crm.sat.responseRate")} value={kpis.response_rate == null ? "—" : `${kpis.response_rate}%`} color="#fbbf24" />
        </div>
      )}
      {kpis && kpis.by_question?.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">{t("crm.sat.byQuestion")}</div>
          <div className="space-y-2">
            {kpis.by_question.map((q) => {
              const lbl = pl(lang, SAT_QUESTIONS.find((x) => x.k === q.key)) || q.key;
              return (
                <div key={q.key} className="flex items-center gap-3">
                  <span className="text-xs w-48 shrink-0 text-foreground/70 truncate">{lbl}</span>
                  <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(q.avg / 5) * 100}%`, background: "linear-gradient(90deg,#34d399,#22d3ee)" }} />
                  </div>
                  <span className="text-xs font-semibold w-10 text-right">{q.avg}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Liste */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((i) => {
          const pending = i.is_external && i.response_status !== "responded";
          const al = alertStyle[i.alert];
          const Icon = al?.icon;
          return (
            <div key={i.id} className="rounded-2xl border border-white/10 p-4 bg-white/[0.02] hover:border-emerald-400/30 transition cursor-pointer" data-testid={`crm-sat-${i.id}`} onClick={() => !pending && openEdit(i)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{i.partner_name || "—"}</div>
                  <div className="text-[11px] text-muted-foreground">{pl(lang, satContextEntry(i.context)) || i.context} · {i.date}</div>
                </div>
                {pending ? (
                  <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: "#fbbf24", background: "rgba(245,158,11,0.14)" }}><Clock className="h-3 w-3" /> {t("crm.sat.pending")}</span>
                ) : (
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="font-display text-lg font-bold" style={{ color: al?.color || "#cbd5e1" }}>{i.avg}</span>
                    <Star className="h-4 w-4" style={{ fill: "#fbbf24", color: "#fbbf24" }} />
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between">
                {pending
                  ? <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {i.sent_to}</span>
                  : (al && Icon && <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: al.color, background: al.bg }}><Icon className="h-3 w-3" /> {t(`crm.sat.alert.${i.alert}`)}</span>)}
                {i.maison_name && <span className="text-[11px] text-muted-foreground">{i.maison_name}</span>}
              </div>
              {!readOnly && (
                <div className="mt-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" onClick={() => del(i)} data-testid={`crm-sat-delete-${i.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
          );
        })}
        {!items.length && <p className="text-muted-foreground text-sm col-span-full py-8 text-center">{t("crm.sat.empty")}</p>}
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smile className="h-5 w-5 text-emerald-400" /> {editId ? t("crm.sat.edit") : t("crm.sat.new")}</DialogTitle>
            <DialogDescription className="sr-only">{t("crm.sat.new")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("crm.sat.partner")} <span className="text-rose-400">*</span></Label>
                <Select value={form.partner_id || "none"} onValueChange={(v) => setForm({ ...form, partner_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="sat-partner"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.sat.residence")}</Label>
                <Select value={form.maison_id || "none"} onValueChange={(v) => setForm({ ...form, maison_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="sat-residence"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{maisons.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.sat.context")}</Label>
                <Select value={form.context} onValueChange={(v) => setForm({ ...form, context: v })}>
                  <SelectTrigger data-testid="sat-context"><SelectValue /></SelectTrigger>
                  <SelectContent>{SAT_CONTEXTS.map((c) => <SelectItem key={c.k} value={c.k}>{pl(lang, c)}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.sat.date")}</Label><input type="date" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2.5" data-testid="sat-questions">
              {SAT_QUESTIONS.map((qq) => (
                <div key={qq.k} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-foreground/80">{pl(lang, qq)}</span>
                  <Stars value={form[qq.k] || 0} onChange={(n) => setForm({ ...form, [qq.k]: n })} testid={`sat-${qq.k}`} />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1.5"><Label>{t("crm.sat.positives")}</Label><Textarea rows={2} value={form.positives} onChange={(e) => setForm({ ...form, positives: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t("crm.sat.improvements")}</Label><Textarea rows={2} value={form.improvements} onChange={(e) => setForm({ ...form, improvements: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{t("crm.sat.comment")}</Label><Textarea rows={2} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm"><Checkbox checked={form.anonymous} onCheckedChange={(v) => setForm({ ...form, anonymous: !!v })} data-testid="sat-anonymous" /> {t("crm.sat.anonymous")}</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("crm.actions.cancel")}</Button>
            {!readOnly && <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-500" data-testid="sat-save">{t("crm.actions.save")}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog envoi enquête externe */}
      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-emerald-400" /> {t("crm.sat.requestTitle")}</DialogTitle>
            <DialogDescription>{t("crm.sat.requestDesc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>{t("crm.sat.partner")} <span className="text-rose-400">*</span></Label>
              <Select value={req.partner_id || "none"} onValueChange={onReqPartner}>
                <SelectTrigger data-testid="sat-req-partner"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">—</SelectItem>{partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>{t("crm.sat.email")} <span className="text-rose-400">*</span></Label>
              <input type="email" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={req.email} onChange={(e) => setReq({ ...req, email: e.target.value })} data-testid="sat-req-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("crm.sat.residence")}</Label>
                <Select value={req.maison_id || "none"} onValueChange={(v) => setReq({ ...req, maison_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="sat-req-residence"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{maisons.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t("crm.sat.context")}</Label>
                <Select value={req.context} onValueChange={(v) => setReq({ ...req, context: v })}>
                  <SelectTrigger data-testid="sat-req-context"><SelectValue /></SelectTrigger>
                  <SelectContent>{SAT_CONTEXTS.map((c) => <SelectItem key={c.k} value={c.k}>{pl(lang, c)}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqOpen(false)}>{t("crm.actions.cancel")}</Button>
            <Button onClick={sendReq} className="bg-emerald-600 hover:bg-emerald-500" data-testid="sat-req-send"><Send className="h-4 w-4 mr-1" /> {t("crm.sat.send")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
